import { Card, type CardRootProps } from "@heroui/react";
import { type AppCardVariant } from "./variants";

/**
 * Constrained card root props.
 * className escape hatch available for one-off overrides.
 */
export type AppCardProps = Omit<CardRootProps, "className" | "variant"> & {
  /** Visual variant. @default "default" */
  variant?: AppCardVariant;
  /** Optional className escape hatch for one-off overrides. */
  className?: string;
};

/**
 * AppCard root — Design system card container.
 *
 * Wraps HeroUI v3 Card with constrained Rosé Pine variants.
 * Sub-components (Header, Title, Description, Content, Footer) are
 * pass-throughs from HeroUI — they inherit slot styling from CardContext.
 *
 * @example
 * ```tsx
 * <AppCard variant="secondary">
 *   <AppCard.Header>
 *     <AppCard.Title>Title</AppCard.Title>
 *   </AppCard.Header>
 *   <AppCard.Content>
 *     <p>Card content</p>
 *   </AppCard.Content>
 *   <AppCard.Footer>
 *     <AppButton variant="primary" size="sm">Action</AppButton>
 *   </AppCard.Footer>
 * </AppCard>
 * ```
 */
function AppCardRoot({
  variant = "default",
  className,
  ...props
}: AppCardProps) {
  return <Card {...props} variant={variant} className={className} />;
}

/**
 * Compound AppCard with sub-components.
 * Sub-components are direct HeroUI pass-throughs (className allowed for layout).
 */
export const AppCard = Object.assign(AppCardRoot, {
  Header: Card.Header,
  Title: Card.Title,
  Description: Card.Description,
  Content: Card.Content,
  Footer: Card.Footer,
});
